import type { AxiosError } from 'axios';

import { ApiStatus } from '~/constants';
import { AUTH_SESSION } from '~/reducers/auth/types';
import { INITIALIZATION_ERROR } from '~/reducers/nav/types';
import { updateConfig } from '~/services/config';
import { getAuthSession } from '~/utilities/session';
import { store } from '~/utilities/store';

const root = async (pageCtx: PageContext) => {
  const queryParams = pageCtx.query;

  const {
    country,
    appVersion,
    deviceManufacturer,
    deviceModel,
    osType,
    osVersion,
  } = queryParams ?? {};

  try {
    await updateConfig({
      compatibility: true,
      country,
      appVersion,
      deviceManufacturer,
      deviceModel,
      osType,
      osVersion,
    });
  } catch (err) {
    const error = err as AxiosError;

    setupInitializationErrorLogic({ errorStatus: error.status ?? 500 });
  }
};

const setupInitializationErrorLogic = ({
  errorStatus,
  queryString,
}: {
  errorStatus: number;
  queryString?: string;
}) => {
  store.dispatch({
    type: INITIALIZATION_ERROR,
    initErrorStatus: errorStatus,
    queryString,
  });
};

const setupLoginLogic = (pageCtx: PageContext) => {
  let url = '/login';

  if (pageCtx.querystring) {
    url = url.concat(`?${pageCtx.querystring}`);
  }

  pageCtx.page.redirect(url);
};

const protectedPageAction = async (pageCtx: PageContext) => {
  const queryParams = pageCtx.query;
  const authSession = getAuthSession();

  if (!queryParams) {
    setupInitializationErrorLogic({ errorStatus: ApiStatus.BAD_REQUEST });

    return;
  }

  if (!authSession) {
    setupLoginLogic(pageCtx);

    return;
  }

  // FIXME:
  // This is a temporary solution to populate auth session in the store
  // Properly we should use the `getPatient` service to fetch the patient data
  store.dispatch({ type: AUTH_SESSION, authSession });

  const {
    appVersion,
    deviceManufacturer,
    deviceModel,
    osType,
    osVersion,
    deviceId,
  } = queryParams;

  return updateConfig({
    compatibility: true,
    country: authSession.include?.patient?.country,
    appVersion,
    deviceManufacturer,
    deviceModel,
    osType,
    osVersion,
    deviceId,
  });
};

// Create your paths that you want to handle here
// you want to most specific earlier and the least specific later
// Note: if one action throws an error (calls promise reject) then the handlePaths
// will move onto the next matching route
const routeActions: RouteAction[] = [
  {
    regex: [
      /^\/account-information/i,
      /^\/update-password/i,
      /^\/rwe/i,
      /^\/hipaa/i,
      /^\/consents/i,
      /^\/verify-account/i,
      /^\/update-phone/i,
      /^\/update-email/i,
      /^\/add-connection/i,
      /^\/connections/i,
    ],
    action: protectedPageAction,
  },
  {
    regex: [/^\.*/],
    action: root,
  },
];

type RouteAction = {
  regex: RegExp[];
  action: RouteActionFn;
};

type RouteActionFn = (ctx: PageContext) => Promise<void>;

export const handlePaths = (ctx: PageContext): Promise<void> => {
  const doActions: RouteActionFn[] = [];

  for (const check of routeActions) {
    for (const r of check.regex) {
      if (RegExp(r).exec(ctx.canonicalPath)) {
        // Add valid actions to chain later
        doActions.push(check.action);
      }
    }
  }

  // Start with a rejected promise
  let p = Promise.reject<void>();

  // Build a negative promise chain that will try the next action if the previous one failed
  // Once an action succeeds the chain ends and the app starts
  for (const element of doActions) {
    p = p.catch(() => element(ctx));
  }

  return p;
};
